import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, type ReactNode } from 'react';

import { ConnectedServicesSync } from '@/components/ConnectedServicesSync';
import { SettingsSync } from '@/components/SettingsSync';
import {
  SETTINGS_DEFAULTS,
  settingsQueryOptions,
  type EffectiveSettings,
} from '@/hooks/queries/use-settings-query';
import { useSessionStore } from '@/stores/session';

const SettingsContext = createContext<EffectiveSettings | null>(null);

export function useSettings(): EffectiveSettings {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return ctx;
}

interface SettingsProviderProps {
  children: ReactNode;
}

/**
 * `SettingsProvider` exposes the `EffectiveSettings` cache via React context
 * AND mounts `<SettingsSync>` (TDEV-266 Phase 6 — plan.md:1380-1389) so the
 * three local zustand stores hydrate from the same query result and
 * propagate user-driven edits back via debounced PATCH. The Sync component
 * is render-only — it does not transform `children`.
 */
export function SettingsProvider({ children }: SettingsProviderProps) {
  const sessionStatus = useSessionStore((s) => s.status);
  const { data } = useQuery({
    ...settingsQueryOptions,
    enabled: sessionStatus === 'active',
  });

  return (
    <SettingsContext.Provider value={data ?? SETTINGS_DEFAULTS}>
      <ConnectedServicesSync enabled={sessionStatus === 'active'} />
      <SettingsSync>{children}</SettingsSync>
    </SettingsContext.Provider>
  );
}
